"use client";

import React, { useEffect, useState } from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Calendar, Clock, User, Tag, ArrowRight, Share2, Facebook, Twitter, Linkedin } from "lucide-react";



function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="27034" className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((_, i) => (
          <div key={i} data-index={i} className="h-96 w-full animate-pulse rounded-xl bg-muted/50" />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return null; 
  }

  return (
    <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="group overflow-hidden border-none bg-card shadow-sm transition-all duration-300 hover:shadow-lg">
          <div className="relative aspect-[16/9] overflow-hidden">
            {post.featuredImage ? (
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                <span className="text-sm font-default">Geen afbeelding</span>
              </div>
            )}
            <div className="absolute top-4 left-4 rounded bg-primary px-3 py-1 text-xs font-default font-bold text-primary-foreground uppercase tracking-wider">
              {post.categories?.[0]?.name || "Insights"}
            </div>
          </div>
          <CardHeader className="p-6 pb-3">
            <div className="mb-2 flex items-center gap-2 text-xs font-default text-muted-foreground">
              <Calendar className="h-3 w-3" />
              <span>{post.date ? new Date(post.date).toLocaleDateString('nl-NL') : "Recent"}</span>
            </div>
            <Link to={post.link || "#"}>
              <CardTitle className="line-clamp-2 text-xl font-default font-bold text-foreground transition-colors group-hover:text-primary">
                {post.title}
              </CardTitle>
            </Link>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            <div 
              className="line-clamp-3 text-sm font-default text-muted-foreground"
              dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
            />
          </CardContent>
          <CardFooter className="p-6 pt-0">
            <Link to={post.link || "#"} className="group/btn flex items-center text-sm font-default font-bold text-primary uppercase tracking-tight">
              Lees Meer 
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsWrapper({ currentPostId, categoryIds }: { currentPostId: string; categoryIds: number[] }) {
  
  const relatedQuery = new WP_Query({
    posts_per_page: 3,
    post__not_in: [currentPostId],
    category__in: categoryIds,
    orderby: 'date',
    order: 'DESC',
    post_type: 'post' 
  });

  return (
    <div className="mt-24">
      <div className="mb-10 flex items-center justify-between">
        <h3 className="text-2xl font-default font-bold text-foreground md:text-3xl">Gerelateerde Artikelen</h3>
        <Link to="/blog">
          <Button variant="outline" className="hidden sm:flex font-default">Bekijk alle artikelen</Button>
        </Link>
      </div>
      <PostsProvider wp_query={relatedQuery}>
        <RelatedPostsConsumer />
      </PostsProvider>
    </div>
  );
}



function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-8 py-12">
        <div className="h-12 w-3/4 animate-pulse rounded-lg bg-muted/50" />
        <div className="h-96 w-full animate-pulse rounded-xl bg-muted/50" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded bg-muted/50" />
          <div className="h-4 w-full animate-pulse rounded bg-muted/50" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-muted/50" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 text-center">
        <h2 className="text-2xl font-default font-bold text-destructive">Fout bij laden bericht</h2>
        <p className="text-muted-foreground font-default">{error}</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="py-20 text-center">
        <h2 className="text-2xl font-default font-bold text-foreground">Bericht niet gevonden</h2>
        <Link to="#">
          <Button className="mt-4 font-default">Terug naar Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const categoryIds = post.categories?.map(c => c.id) || [];

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = post.title;

  return (
    <article className="animate-in fade-in slide-in-from-bottom-4 duration-700">
      
      <header className="mx-auto max-w-4xl text-center">
        <div className="mb-6 flex flex-wrap items-center justify-center gap-4 text-sm font-default font-medium text-muted-foreground">
          {post.categories && post.categories.length > 0 && (
            <span className="flex items-center gap-1.5 text-primary uppercase tracking-widest font-bold">
              <Tag className="h-4 w-4" />
              {post.categories[0].name}
            </span>
          )}
          <span className="h-1 w-1 rounded-full bg-muted-foreground/30" />
          <span className="flex items-center gap-1.5">
            <Calendar className="h-4 w-4" />
            {post.date ? new Date(post.date).toLocaleDateString('nl-NL', { year: 'numeric', month: 'long', day: 'numeric' }) : ""}
          </span>
          <span className="h-1 w-1 rounded-full bg-muted-foreground/30" />
          <span className="flex items-center gap-1.5">
            <Clock className="h-4 w-4" />
            5 min leestijd
          </span>
        </div>

        <h1 className="mb-8 text-3xl font-default font-bold leading-tight text-foreground md:text-4xl lg:text-5xl tracking-tight">
          {post.title}
        </h1>

        {post.featuredImage && (
          <div className="relative mb-12 overflow-hidden rounded-xl shadow-lg">
            <img
              src={post.featuredImage}
              alt={post.title}
              className="h-auto w-full object-cover"
            />
          </div>
        )}
      </header>

      
      <div className="mx-auto max-w-3xl">
        <div 
          className="prose prose-lg prose-neutral max-w-none 
          prose-headings:font-default prose-headings:font-bold prose-headings:text-foreground prose-headings:tracking-tight
          prose-p:font-default prose-p:text-muted-foreground prose-p:leading-relaxed
          prose-a:text-primary prose-a:font-medium prose-a:no-underline hover:prose-a:underline
          prose-strong:text-foreground prose-strong:font-bold
          prose-img:rounded-xl prose-img:shadow-md
          prose-blockquote:border-l-primary prose-blockquote:bg-muted/30 prose-blockquote:py-2 prose-blockquote:px-6 prose-blockquote:not-italic prose-blockquote:text-foreground
          [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6"
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />

        
        <div className="mt-12 flex flex-col gap-6 border-y border-border py-8 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-2">
            {post.tags && post.tags.map((tag, i) => (
              <span key={i} data-index={i} className="rounded-full bg-muted px-3 py-1 text-xs font-default font-medium text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary">
                #{tag.name}
              </span>
            ))}
          </div>
          
          <div className="flex items-center gap-3">
            <span className="text-sm font-default font-bold text-foreground">Delen:</span>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="icon" 
                className="h-8 w-8 rounded-full border-muted-foreground/20 text-muted-foreground transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground"
                onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400')}
              >
                <Facebook className="h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                size="icon" 
                className="h-8 w-8 rounded-full border-muted-foreground/20 text-muted-foreground transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground"
                onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`, '_blank', 'width=600,height=400')}
              >
                <Twitter className="h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                size="icon" 
                className="h-8 w-8 rounded-full border-muted-foreground/20 text-muted-foreground transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground"
                onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400')}
              >
                <Linkedin className="h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                size="icon" 
                className="h-8 w-8 rounded-full border-muted-foreground/20 text-muted-foreground transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground"
                onClick={() => navigator.clipboard.writeText(shareUrl)}
              >
                <Share2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        
        {post.author && (
          <div className="mt-12 rounded-xl bg-card border border-border/50 p-8 shadow-sm">
            <div className="flex flex-col items-center gap-6 text-center sm:flex-row sm:text-left">
              <Avatar className="h-20 w-20 border-2 border-background shadow-sm">
                <AvatarImage src={post.author.avatar?.['96'] || ""} alt={post.author.name} />
                <AvatarFallback className="bg-primary text-xl font-default font-bold text-primary-foreground">
                  {post.author.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="mb-1 text-xs font-default font-bold text-primary uppercase tracking-widest">Over de auteur</div>
                <h3 className="mb-2 text-xl font-default font-bold text-foreground">{post.author.name}</h3>
                <p className="text-sm font-default leading-relaxed text-muted-foreground">
                  Gepassioneerd door digitale groei en data-gedreven marketing. Met een focus op persoonlijke benadering en technologische innovatie helpt Connected Online de bedrijven om hun volledige potentieel te bereiken in een steeds veranderende online wereld.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <RelatedPostsWrapper currentPostId={post.id} categoryIds={categoryIds} />
      </div>
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  
  const currentParams = typeof window !== 'undefined' && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post" };

  const wpQuery = new WP_Query(currentParams);

  return (
    <section id="single-post-template-section" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4">
        <PostsProvider wp_query={wpQuery}>
          <SinglePostConsumer />
        </PostsProvider>
      </div>
    </section>
  );
}